import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { AuthError, Session, User } from "@supabase/supabase-js";
import { STRONOM_IDENTITY_BINDING } from "@/features/footwear-studio/featureFlags";
import { getSupabaseClient } from "@/lib/supabase";
import { apiFetch } from "@/lib/apiFetch";

const SUPABASE_NOT_CONFIGURED = "Supabase not configured";

type SignInResult = { error?: AuthError | string };
type SignUpResult =
  | { error: AuthError | string }
  | { needsEmailConfirmation: boolean };
type SignOutResult = { error?: AuthError | string };

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  loading: boolean;
  orgKey: string | null;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  signUp: (email: string, password: string) => Promise<SignUpResult>;
  signOut: () => Promise<SignOutResult>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

async function bootstrapProfileOrgKey(session: Session): Promise<string | null> {
  if (!STRONOM_IDENTITY_BINDING) {
    return null;
  }

  try {
    const res = await apiFetch("/api/profile/bootstrap", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    const data = (await res.json().catch(() => null)) as {
      ok?: boolean;
      disabled?: boolean;
      org_key?: string;
      error?: string;
    } | null;

    if (!res.ok) {
      console.log(
        `[IDENTITY_BINDING] bootstrap_error=${data?.error ?? `http_${res.status}`}`
      );
      return null;
    }

    if (data?.disabled) {
      return null;
    }

    if (typeof data?.org_key === "string" && data.org_key.length > 0) {
      return data.org_key;
    }

    return null;
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    console.log(`[IDENTITY_BINDING] bootstrap_error=${msg}`);
    return null;
  }
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [orgKey, setOrgKey] = useState<string | null>(null);

  useEffect(() => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      if (cancelled) return;
      setSession(initialSession);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!STRONOM_IDENTITY_BINDING || !session?.access_token) {
      setOrgKey(null);
      return;
    }

    let cancelled = false;

    bootstrapProfileOrgKey(session).then((key) => {
      if (!cancelled) {
        setOrgKey(key);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [session?.access_token, session?.user?.id]);

  const signIn = useCallback(async (email: string, password: string) => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      return { error: SUPABASE_NOT_CONFIGURED };
    }

    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error };
    return {};
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      return { error: SUPABASE_NOT_CONFIGURED };
    }

    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) return { error };
    return { needsEmailConfirmation: data.session === null };
  }, []);

  const signOut = useCallback(async () => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      return { error: SUPABASE_NOT_CONFIGURED };
    }

    const { error } = await supabase.auth.signOut();
    if (error) return { error };
    setOrgKey(null);
    return {};
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      orgKey,
      signIn,
      signUp,
      signOut,
    }),
    [session, loading, orgKey, signIn, signUp, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
